import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import React from 'react';

import {isInaccessibleInfo} from 'auth/RequestAccess';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import notifications from 'notifications';
import preferences from 'Preferences';
import {
  RecentlyUsedRequestV2DefinitionsQuery,
  RecentlyUsedRequestV2DefinitionsQueryVariables,
  RecentlyUsedRequestV2DefinitionsQuery_requestV2Definitions as RequestV2DefinitionOrInaccessible,
} from 'processes/requests_v2/dashboard/__generated/RecentlyUsedRequestV2DefinitionsQuery';
import CreateRequestV2SubmissionModal from 'processes/requests_v2/modals/CreateRequestV2SubmissionModal';
import Label from 'ui-library/bs/Label';
import {GRAY_LIGHT, GRAY_LIGHTER, GRAY_SEMIDARK, WHITE} from 'ui-library/Colors';
import {Header1} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import runModal from 'ui-library/runModal';
import {ellipsize} from 'util/NameUtil';

interface RequestV2DefinitionCardProps {
  requestDefinitionOrInaccessible: RequestV2DefinitionOrInaccessible;
}

export function RequestV2DefinitionCard(props: RequestV2DefinitionCardProps): JSX.Element {
  return (
    <div className={css(styles.requestDefinitionCard)}>
      {isInaccessibleInfo(props.requestDefinitionOrInaccessible) ? (
        <span>Inaccessible request template</span>
      ) : (
        <div
          className={css(styles.requestDefinitionCardText)}
          onClick={() => {
            assert(!isInaccessibleInfo(props.requestDefinitionOrInaccessible), '');
            runModal(CreateRequestV2SubmissionModal, {
              actionOrigin: ActionOrigin.REQUEST_V2_SIDEPANEL,
              initialRequestV2DefinitionId: props.requestDefinitionOrInaccessible.id,
            });
          }}
        >
          <Header1>{props.requestDefinitionOrInaccessible.name}</Header1>
          <span className={css(styles.requestDefinitionDescription)}>
            {ellipsize(props.requestDefinitionOrInaccessible.description ?? '', 200)}
          </span>
        </div>
      )}
    </div>
  );
}

interface RecentlyUsedRequestV2DefinitionsCardViewProps {
  requestDefinitionsOrInaccessible: Array<RequestV2DefinitionOrInaccessible>;
}

function RecentlyUsedRequestV2DefinitionsCardView(
  props: RecentlyUsedRequestV2DefinitionsCardViewProps
): JSX.Element {
  return (
    <div className={css(styles.requestDefinitionCardRow)}>
      {props.requestDefinitionsOrInaccessible.length === 0 && (
        <span className={css(styles.requestDefinitionDescription)}>
          Recently used request types will appear here after you create requests.
        </span>
      )}
      {props.requestDefinitionsOrInaccessible.map((requestDefinitionOrInaccessible, index) => (
        <RequestV2DefinitionCard
          requestDefinitionOrInaccessible={requestDefinitionOrInaccessible}
          key={index}
        />
      ))}
    </div>
  );
}

export const RECENTLY_USED_REQUEST_V2_DEFINITIONS_QUERY = gql`
  query RecentlyUsedRequestV2DefinitionsQuery($requestV2DefinitionIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    requestV2Definitions(ids: $requestV2DefinitionIds) {
      ... on RequestV2DefinitionNode {
        id
        name
        description
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function QueryingRecentlyUsedRequestV2DefinitionsCardView(): JSX.Element | null {
  const recentlyUsedRequestIds = preferences.get('RECENTLY_USED_REQUEST_V2_DEFINITIONS') ?? [];
  const result = useBenchlingQuery<
    RecentlyUsedRequestV2DefinitionsQuery,
    RecentlyUsedRequestV2DefinitionsQueryVariables
  >(RECENTLY_USED_REQUEST_V2_DEFINITIONS_QUERY, {
    variables: {requestV2DefinitionIds: recentlyUsedRequestIds},
    fetchPolicy: 'cache-and-network',
  });
  if (result.type === QueryResultType.ERROR) {
    notifications.error('Could not load request templates; try reloading the page.');
    return null;
  }
  return (
    <div>
      <Label className={css(styles.header)}>Recently Used</Label>
      {result.type === QueryResultType.PENDING && <CenteredHorizontalThrobber />}
      {result.type === QueryResultType.SUCCESS && (
        <RecentlyUsedRequestV2DefinitionsCardView
          requestDefinitionsOrInaccessible={result.data.requestV2Definitions}
        />
      )}
      <Label className={css(styles.header)}>All request types</Label>
    </div>
  );
}

const styles = StyleSheet.create({
  header: {
    padding: '16px 0px',
    marginBottom: 0,
  },
  requestDefinitionCard: {
    width: '25%',
    minWidth: 140,
    height: 140,
    padding: 16,
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: 4,
    backgroundColor: WHITE,
    ':hover': {
      backgroundColor: GRAY_LIGHTER,
    },
  },
  noRecentlyUsedRequestsMessage: {
    color: GRAY_SEMIDARK,
  },
  requestDefinitionDescription: {
    display: 'inline-block',
    color: GRAY_SEMIDARK,
    height: '75%',
    width: '100%',
    overflow: 'hidden',
  },
  requestDefinitionCardRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: 16,
  },
  requestDefinitionCardText: {
    height: '100%',
    width: '100%',
  },
});
